const resources = {
  "en": {
    "categoryManager": {
      "title": "Categories",
      "subtitle": "Manage category meaning so analysis and planning stay understandable and trustworthy.",
      "sectionTitle": "Category meaning for analysis",
      "sectionDescription": "Choose how each category should behave in Finance Analysis before it turns into fixed, flexible, optional, refund, or peer-money logic.",
      "filterBar": {
        "leading": "Choose the category set you are shaping",
        "summary_one": "{{count}} {{typeLabel}} category in this view",
        "summary_other": "{{count}} {{typeLabel}} categories in this view"
      },
      "types": {
        "expense": "Expense",
        "income": "Income"
      },
      "actions": {
        "add": "Add",
        "reorder": "Reorder",
        "done": "Done",
        "saveOrder": "Save order",
        "edit": "Edit",
        "delete": "Delete"
      },
      "list": {
        "empty": "No categories"
      },
      "semanticInfo": {
        "title": "Category meaning powers analysis",
        "description": "Choose what each category means for analysis and planning. This is how Finance Analysis decides whether something is fixed, variable, optional, peer money, or refund-related."
      },
      "reorder": {
        "noticeTitle": "Set the order your forms will use",
        "noticeDescription": "On smaller screens you can still move categories up and down. This order will shape the category list in transaction forms for the current type.",
        "noticeActiveDescription": "Move categories into the order you want, then save it. Transaction forms will use the same order for this type.",
        "noticePendingDescription": "You changed the order. Save it to update the category list used in transaction forms for this type.",
        "moveUp": "Move category up",
        "moveDown": "Move category down",
        "dragHandle": "Drag category"
      },
      "modal": {
        "addTitle": "Add Category",
        "editTitle": "Edit Category",
        "save": "Save"
      },
      "deleteModal": {
        "title": "Delete category",
        "description": "Remove \"{{name}}\" only after you understand what else depends on it.",
        "loadingImpact": "Checking where this category is still used…",
        "inUseTitle": "This category is still in use",
        "inUseDescription": "To delete it safely, move every linked record to another category of the same type first.",
        "unusedTitle": "This category is safe to delete",
        "unusedDescription": "No linked transactions, recurring items, or task records were found.",
        "replacementLabel": "Move linked records to",
        "replacementHint": "Transactions, recurring items, and task records will be reassigned before this category is removed.",
        "replacementPlaceholder": "Choose replacement category",
        "confirmDelete": "Delete category",
        "confirmReplace": "Replace and delete",
        "noReplacementTitle": "Create another category first",
        "noReplacementDescription": "This category is still used. Add another category of the same type, then reassign before deleting.",
        "usage": {
          "transactions": "Transactions",
          "recurrentExpenses": "Recurring expenses",
          "recurrentIncomes": "Recurring incomes",
          "tasks.pending": "Pending tasks",
          "tasks.overdue": "Overdue tasks",
          "tasks.done": "Completed tasks"
        }
      },
      "form": {
        "fields": {
          "name": {
            "label": "Name",
            "required": "Please enter a category name"
          },
          "meaning": {
            "label": "Meaning for analysis",
            "required": "Please choose what this category means",
            "legendTitle": "Meaning guide",
            "legendDescription": "These meanings drive how Finance Analysis groups this category later.",
            "currentSelection": "Current selection"
          }
        }
      },
      "meaning": {
        "expense": {
          "otherExpense": {
            "label": "Other expense",
            "description": "Normal expense that does not belong to a stronger planning bucket."
          },
          "fixedEssential": {
            "label": "Fixed essential",
            "description": "Regular obligations like rent, bills, insurance, phone, or home internet."
          },
          "variableEssential": {
            "label": "Variable essential",
            "description": "Necessary spending that changes month to month, like groceries, transport, or medical."
          },
          "discretionary": {
            "label": "Optional spending",
            "description": "Non-essential spending like entertainment, restaurants, and optional purchases."
          },
          "reimbursementOut": {
            "label": "Reimbursable outflow",
            "description": "Money you paid out but expect to get back later."
          },
          "refundOut": {
            "label": "Refund reversal",
            "description": "Outgoing reversal tied to a refund workflow."
          },
          "debtInterest": {
            "label": "Interest / fees",
            "description": "Interest, finance charges, and debt-related fees."
          }
        },
        "income": {
          "earnedIncome": {
            "label": "Earned income",
            "description": "Salary, freelance income, payroll, or normal business earnings."
          },
          "peerInflow": {
            "label": "Peer money",
            "description": "Money from other people that should not be treated as earned income."
          },
          "reimbursementIn": {
            "label": "Reimbursement",
            "description": "Money returned to cover spending you already made."
          },
          "refundIn": {
            "label": "Refund",
            "description": "Money returned after a purchase, charge, or reversal."
          }
        }
      },
      "recurrence": {
        "scheduled": "Scheduled tendency",
        "observed_recurring": "Observed recurring"
      },
      "messages": {
        "loadFailed": "Failed to load categories",
        "createSuccess": "Category added",
        "updateSuccess": "Category updated",
        "deleteSuccess": "Category deleted",
        "replaceAndDeleteSuccess": "Category replaced and deleted",
        "deleteNeedsReplacement": "Choose a replacement category before deleting",
        "orderSaved": "Category order saved",
        "orderSaveFailed": "Failed to save category order",
        "errorWithReason": "{{error}}",
        "defaultError": "Something went wrong"
      }
    }
  },
  "uk": {
    "categoryManager": {
      "title": "Категорії",
      "subtitle": "Керуйте сенсом категорій, щоб аналіз і планування залишалися зрозумілими та правдивими.",
      "sectionTitle": "Сенс категорій для аналізу",
      "sectionDescription": "Оберіть, як кожна категорія має поводитися у Finance Analysis, перш ніж вона перетвориться на fixed, flexible, optional, refund або peer-money логіку.",
      "filterBar": {
        "leading": "Оберіть набір категорій, який зараз налаштовуєте",
        "summary_one": "{{count}} категорія типу {{typeLabel}} у цьому вигляді",
        "summary_few": "{{count}} категорії типу {{typeLabel}} у цьому вигляді",
        "summary_many": "{{count}} категорій типу {{typeLabel}} у цьому вигляді",
        "summary_other": "{{count}} категорії типу {{typeLabel}} у цьому вигляді"
      },
      "types": {
        "expense": "Витрати",
        "income": "Дохід"
      },
      "actions": {
        "add": "Додати",
        "reorder": "Змінити порядок",
        "done": "Готово",
        "saveOrder": "Зберегти порядок",
        "edit": "Редагувати",
        "delete": "Видалити"
      },
      "list": {
        "empty": "Категорій ще немає"
      },
      "semanticInfo": {
        "title": "Сенс категорії впливає на аналіз",
        "description": "Оберіть, що означає кожна категорія для аналізу та планування. Так Finance Analysis вирішує, чи це fixed, variable, optional, peer money або refund-related рух."
      },
      "reorder": {
        "noticeTitle": "Визначте порядок для форм",
        "noticeDescription": "На малих екранах категорії можна рухати кнопками вгору та вниз. Саме цей порядок потім використовуватимуть форми транзакцій для поточного типу.",
        "noticeActiveDescription": "Розташуйте категорії в потрібному порядку й збережіть його. Форми транзакцій використовуватимуть той самий порядок для цього типу.",
        "noticePendingDescription": "Порядок змінено. Збережіть його, щоб оновити список категорій у формах транзакцій для цього типу.",
        "moveUp": "Підняти категорію",
        "moveDown": "Опустити категорію",
        "dragHandle": "Перетягнути категорію"
      },
      "modal": {
        "addTitle": "Додати категорію",
        "editTitle": "Редагувати категорію",
        "save": "Зберегти"
      },
      "deleteModal": {
        "title": "Видалити категорію",
        "description": "Видаляйте \"{{name}}\" лише після того, як зрозумієте, що ще від неї залежить.",
        "loadingImpact": "Перевіряємо, де ця категорія ще використовується…",
        "inUseTitle": "Ця категорія ще використовується",
        "inUseDescription": "Щоб видалити її безпечно, спочатку переведіть усі пов'язані записи в іншу категорію того ж типу.",
        "unusedTitle": "Цю категорію можна безпечно видалити",
        "unusedDescription": "Не знайдено пов'язаних транзакцій, recurring-елементів або записів задач.",
        "replacementLabel": "Перенести пов'язані записи в",
        "replacementHint": "Транзакції, recurring-елементи та записи задач буде перев'язано перед видаленням цієї категорії.",
        "replacementPlaceholder": "Оберіть категорію для заміни",
        "confirmDelete": "Видалити категорію",
        "confirmReplace": "Замінити й видалити",
        "noReplacementTitle": "Спочатку створіть іншу категорію",
        "noReplacementDescription": "Ця категорія ще використовується. Додайте іншу категорію того ж типу, а потім перенесіть записи перед видаленням.",
        "usage": {
          "transactions": "Транзакції",
          "recurrentExpenses": "Recurring витрати",
          "recurrentIncomes": "Recurring доходи",
          "tasks.pending": "Очікуючі задачі",
          "tasks.overdue": "Прострочені задачі",
          "tasks.done": "Завершені задачі"
        }
      },
      "form": {
        "fields": {
          "name": {
            "label": "Назва",
            "required": "Введіть назву категорії"
          },
          "meaning": {
            "label": "Сенс для аналізу",
            "required": "Оберіть, що означає ця категорія",
            "legendTitle": "Пояснення сенсів",
            "legendDescription": "Саме ці сенси визначають, як Finance Analysis потім групуватиме цю категорію.",
            "currentSelection": "Поточний вибір"
          }
        }
      },
      "meaning": {
        "expense": {
          "otherExpense": {
            "label": "Інша витрата",
            "description": "Звичайна витрата, яка не належить до сильнішого planning bucket."
          },
          "fixedEssential": {
            "label": "Фіксована необхідна",
            "description": "Регулярні зобов'язання на кшталт оренди, рахунків, страхування, телефону або домашнього інтернету."
          },
          "variableEssential": {
            "label": "Змінна необхідна",
            "description": "Необхідні витрати, які змінюються від місяця до місяця: продукти, транспорт або медицина."
          },
          "discretionary": {
            "label": "Необов'язкові витрати",
            "description": "Необов'язкові витрати на кшталт розваг, ресторанів та опціональних покупок."
          },
          "reimbursementOut": {
            "label": "Витрата з відшкодуванням",
            "description": "Гроші, які ви витратили, але очікуєте повернути пізніше."
          },
          "refundOut": {
            "label": "Зворотний refund-відтік",
            "description": "Вихідний рух, пов'язаний зі сценарієм refund."
          },
          "debtInterest": {
            "label": "Відсотки / комісії",
            "description": "Відсотки, finance charges та боргові комісії."
          }
        },
        "income": {
          "earnedIncome": {
            "label": "Зароблений дохід",
            "description": "Зарплата, фриланс, payroll або звичайний зароблений дохід."
          },
          "peerInflow": {
            "label": "Гроші від людей",
            "description": "Гроші від інших людей, які не варто вважати earned income."
          },
          "reimbursementIn": {
            "label": "Відшкодування",
            "description": "Гроші, що повернулися, щоб покрити вже понесені витрати."
          },
          "refundIn": {
            "label": "Refund",
            "description": "Гроші, повернуті після покупки, списання або реверсу."
          }
        }
      },
      "recurrence": {
        "scheduled": "Scheduled tendency",
        "observed_recurring": "Observed recurring"
      },
      "messages": {
        "loadFailed": "Не вдалося завантажити категорії",
        "createSuccess": "Категорію додано",
        "updateSuccess": "Категорію оновлено",
        "deleteSuccess": "Категорію видалено",
        "replaceAndDeleteSuccess": "Категорію замінено й видалено",
        "deleteNeedsReplacement": "Оберіть категорію для заміни перед видаленням",
        "orderSaved": "Порядок категорій збережено",
        "orderSaveFailed": "Не вдалося зберегти порядок категорій",
        "errorWithReason": "{{error}}",
        "defaultError": "Сталася помилка"
      }
    }
  }
};

export default resources;
